import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Typography from '@mui/material/Typography/Typography';
import Divider from '@mui/material/Divider';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CasesIcon from '@mui/icons-material/CasesRounded';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import HelpIcon from '@mui/icons-material/Help';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NestedMenuItem } from 'mui-nested-menu';
import { useQuery } from '@tanstack/react-query';
import { appContext } from '../Contexts/AppContext';
import { appUrls } from '../utils/constants';
import { useIsInternalUser } from '../utils/helpers';
import { getProjectsList } from '../utils/authenticated-requests/projects';

interface UserMenuProps {
  anchorElUser: null | HTMLElement;
  handleCloseUserMenu: () => void;
  handleOpenCompanySwitch: (e: any) => void;
  setIsCreateProjectModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  signOut: () => void;
}

export const UserMenu = ({
  anchorElUser,
  handleCloseUserMenu,
  handleOpenCompanySwitch,
  setIsCreateProjectModalOpen,
  signOut,
}: UserMenuProps) => {
  const { t, i18n } = useTranslation();
  const isInternalUser: boolean = useIsInternalUser();
  const { company, activeProject, user } = useContext(appContext);

  // Load projects on first page load and when company changes
  const { data: projects, isLoading: isProjectsLoading } = useQuery({
    queryKey: ['projects', company],
    queryFn: () =>
      getProjectsList({
        company,
        queryParams: { type: 'litholens', limit: 10 },
      }),
    select: (data) => data.items,
  });

  const isAdmin = useMemo((): boolean => {
    const admin: boolean =
      user.signInUserSession?.idToken.payload['custom:admin'] === 'true';
    return admin;
  }, [user]);

  /**
   * Updates app language and save selection in local storage
   */
  const handleChangeLanguage = (language: string) => {
    const currentLanguage: string = i18n.language;
    if (
      i18n.hasResourceBundle(language, 'translation') &&
      language !== currentLanguage
    ) {
      i18n.changeLanguage(language);
      if (window.localStorage) {
        window.localStorage.setItem(
          `${user.userDataKey}.selectedLanguage.litholens`,
          language,
        );
      }
    }
  };

  const handleCreateProject = () => {
    handleCloseUserMenu();
    setIsCreateProjectModalOpen(true);
  };

  return (
    <Menu
      sx={{ mt: '45px' }}
      id="menu-appbar"
      anchorEl={anchorElUser}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(anchorElUser)}
      onClose={handleCloseUserMenu}
    >
      <Typography sx={{ px: 2, py: 1 }} fontSize="h5.fontSize">
        {user.username}
      </Typography>
      <Typography sx={{ px: 2, pb: 1 }} color="text.secondary">
        {user.signInUserSession?.idToken.payload['custom:company'] || ''}
      </Typography>
      <Divider />
      <MenuItem
        key="support"
        component="a"
        href="mailto:Software.Sales@ALSGlobal.com"
        onClick={handleCloseUserMenu}
      >
        <ListItemIcon>
          <HelpIcon />
        </ListItemIcon>

        <Typography textAlign="right">{t('BUTTON_SUPPORT')}</Typography>
      </MenuItem>
      {isInternalUser && isAdmin && (
        <MenuItem
          key="admin-app"
          component="a"
          target="_blank"
          href={`${appUrls.admin}`}
          onClick={handleCloseUserMenu}
        >
          <ListItemIcon>
            <AdminPanelSettingsIcon />
          </ListItemIcon>

          <Typography textAlign="right">
            {t('NAVIGATION_ITEM_GOLDSPOT_ADMIN')}
          </Typography>
        </MenuItem>
      )}
      {isInternalUser && (
        <MenuItem key="switch-company" onClick={handleOpenCompanySwitch}>
          <ListItemIcon>
            <ChangeCircleIcon />
          </ListItemIcon>
          <Typography textAlign="right">
            {t('BUTTON_SWITCH_COMPANY')}
          </Typography>
        </MenuItem>
      )}
      <NestedMenuItem
        key="switch-project"
        component="li"
        label={t('BUTTON_SWITCH_PROJECT')}
        parentMenuOpen={Boolean(anchorElUser)}
        className="nested-menu-item"
        leftIcon={
          <ListItemIcon>
            <CasesIcon />
          </ListItemIcon>
        }
        delay={500}
        MenuProps={{
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
        sx={{
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        {!isProjectsLoading && projects && projects.length > 0 ? (
          <>
            {projects.map((project: any, index: number) => (
              <MenuItem
                key={`project-${project.id}-${index}`}
                component={Link}
                to={`/${company}/projects/${project.id}`}
                selected={activeProject?.id === project.id}
                onClick={handleCloseUserMenu}
              >
                {project.title || project.id}
              </MenuItem>
            ))}
            {projects.length >= 10 && (
              <>
                <Divider />
                <MenuItem
                  component={Link}
                  to={`/${company}`}
                  onClick={handleCloseUserMenu}
                >
                  {t('BUTTON_VIEW_MORE')}
                </MenuItem>
              </>
            )}
          </>
        ) : (
          <MenuItem disabled>{t('BUTTON_NO_PROJECTS')}</MenuItem>
        )}
      </NestedMenuItem>
      {isAdmin && (
        <MenuItem key="add-project" onClick={handleCreateProject}>
          <ListItemIcon>
            <MedicalServicesIcon />
          </ListItemIcon>
          <Typography textAlign="right">
            {t('BUTTON_PROJECTS_CREATE_PROJECT')}
          </Typography>
        </MenuItem>
      )}
      <NestedMenuItem
        key="language"
        component="li"
        label={t('BUTTON_LANGUAGE')}
        parentMenuOpen={Boolean(anchorElUser)}
        leftIcon={
          <ListItemIcon>
            <LanguageIcon />
          </ListItemIcon>
        }
        delay={500}
        MenuProps={{
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
        className="nested-menu-item"
        sx={{
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        {(i18n.options.supportedLngs || [])
          .filter((lng: string) => lng !== 'cimode')
          .map((language: string) => {
            return (
              <MenuItem
                value={language}
                key={`language-select-${language}`}
                onClick={() => {
                  handleChangeLanguage(language);
                  handleCloseUserMenu();
                }}
              >
                {t(`OPTION_LANGUAGE_${language}`)}
              </MenuItem>
            );
          })}
      </NestedMenuItem>
      <MenuItem
        key="sign-out"
        onClick={() => {
          handleCloseUserMenu();
          signOut();
        }}
      >
        <ListItemIcon>
          <LogoutIcon />
        </ListItemIcon>
        <Typography textAlign="right">{t('BUTTON_SIGNOUT')}</Typography>
      </MenuItem>
    </Menu>
  );
};
